import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { AddIcon, BookmarkIcon, TemplateStoreNewIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { API } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { CsvImportSelectFile } from './components/csv-import/csv-import';
import { CsvFileMap } from './components/csv-import/csv-map';
import { StartNewFileProps } from './components/start-new-file';
import { WorkspaceData } from './components/workspace';
import { SidebarWrapper } from './components/wrapper';
import { useSidebarStore } from './store';
import { TemplateCategories } from './template-store';
const { useEffect, useState, useCallback } = React;
const StartNewFile = dynamic<StartNewFileProps>(() =>
  import('./components/start-new-file/start-new-file').then((s) => s.StartNewFile)
);
const PrivatePinnedData = dynamic(() =>
  import('./components/private').then((s) => s.PrivatePinnedData)
);
const SharedPinnedData = dynamic(() =>
  import('./components/shared').then((s) => s.SharedPinnedData)
);

export interface Workspaces {
  id: string;
  name: string;
  isOpen?: boolean;
  isCustom?: boolean;
  members: any;
  owner: any;
  access?: string;
  // isPopupOpen: boolean;
  workspaceMenuItems?: { label: string | JSX.Element; value: string; icon?: JSX.Element }[];
}

export interface SideBarProps {
  isOpen: boolean;
  menuItems?: {
    label: string | JSX.Element;
    value: string;
    icon?: JSX.Element;
    href?: string;
  }[];
  hasWorkspaceAccess?: boolean;
  handleToggleSideBar?: ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void) | undefined;
  catalogHref?: string;
  isOverview?: boolean;
}

export const SideBar = ({
  isOpen,
  handleToggleSideBar,
  isOverview = false,
}: SideBarProps): JSX.Element => {
  const router = useRouter();

  const { onChangeOrganisation, onNewFileOpen, onNewFileClose, isNewFileOpen } = useLayout();
  const { fetchSidebar, selectedWorkspaceId, workspaces } = useSidebarStore();
  const [isCsvImportModalOpen, setIsCsvImportModalOpen] = useState<boolean | string>(false);
  const [isProductTourModalOpen, setIsProductTourOpen] = useState(false);
  const [isCsvMapModalOpen, setIsCsvMapModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File>();
  const [fileTitle, setFileTitle] = useState('');
  const [workspace, setWorkspace] = useState<{ label: string; value: string | undefined }>({
    label: '',
    value: '',
  });

  const [openSnackbar] = useSnackbar();

  const authData = useAuth((s) => s.userDetails);
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const [categories, setCategories] = useState<
    {
      name: string;
      color: string;
      slug: string;
    }[]
  >([]);

  const academyMenuItems = [
    {
      label: 'Bookmarks',
      value: 'bookmarks',
      href: `/${mode}/${orgId}/bookmarks`,
      icon: (
        <span className="inline-block ml-2">
          <BookmarkIcon className="mr-1" />
        </span>
      ),
    },
  ];
  const memberRole = authData?.memberInformation?.role;
  const ownerOrganisation = authData?.organisations?.find((org) => org.owner === authData.userId);
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);
  const showDefaultWorkspace = authData?.showDefaultWorkspace;

  useEffect(() => {
    if (!selectedWorkspaceId) {
      fetchSidebar(mode);
    }
  }, []);

  const handleOpenProductTour = () => {
    if (authData?.memberInformation?.productTour?.files)
      setIsProductTourOpen(authData?.memberInformation?.productTour?.files);
  };

  const handleCloseProductTourModal = async () => {
    const payload = {
      type: 'files',
      value: false,
    };
    setIsProductTourOpen(false);
    await API.put('/settings/product-tour', payload);
    fetchUserDetails();
    onNewFileOpen();
  };

  const isActive = useCallback(
    (item: string) => {
      return router.pathname.includes(item);
    },
    [router.pathname, router.query]
  );

  const onShowCsvMap = (file, fileTitle, workspace) => {
    if (!file[0]) {
      setImportFile(undefined);
      openSnackbar({
        text: 'An unexpected error occurred while uploading the file',
        snackType: 'error',
        position: 'bottom-center',
        duration: 2000,
      });
    } else {
      setImportFile(file);
      setIsCsvMapModalOpen(true);
      setIsCsvImportModalOpen(false);
      setFileTitle(fileTitle);
      setWorkspace(workspace);
    }
  };

  const handleImport = (workspaceId?: string) => {
    Analytics.track('Import CSV clicked');
    setIsCsvImportModalOpen(workspaceId || true);
  };

  return (
    <SidebarWrapper isOverview={isOverview} isOpen={isOpen} onToggle={handleToggleSideBar}>
      <>
        <div
          className={classNames('overflow-auto h-full px-14', {
            'custome-scrollbar-1': navigator.userAgent.includes('Win'),
            'no-scrollbar': !navigator.userAgent.includes('Win'),
          })}
        >
          {authData?.authentication !== 'authorised' && router.query.lessons ? (
            <div>
              <p className="font-medium text-xs text-gray-500 mb-8">Categories</p>
              {categories.map((category) => (
                <Link
                  href={
                    authData?.authentication === 'authorised'
                      ? `/${orgId}/catalog/category/${category.slug}`
                      : `/catalog/category/${category.slug}`
                  }
                  key={category.slug}
                >
                  <a
                    className={classNames({
                      'font-medium text-sm text-gray-600 block': true,
                      'text-primary-500 bg-primary-100 hover:bg-blue-200 rounded': isActive(
                        category.slug
                      ),
                    })}
                  >
                    <div className="p-10 min-w-40 flex flex-row">
                      <svg height="20" width="20" className="mr-4">
                        <circle
                          cx="7"
                          cy="10"
                          r="6"
                          stroke={category.color}
                          fill={category.color}
                        />
                      </svg>
                      <span>
                        {category.name.length > 25
                          ? `${category?.name.substring(0, 25)}...`
                          : category.name}
                      </span>
                    </div>
                  </a>
                </Link>
              ))}
            </div>
          ) : authData?.authentication !== 'authorised' && router.query.templates ? (
            <TemplateCategories />
          ) : (
            <></>
          )}
          {authData?.authentication === 'authorised' && (
            <div>
              {/* <section>
                {router.query.mode === 'a' && (
                  <div className="pb-14 border-b">
                    {academyMenuItems.map(({ value, label, icon, href }) => (
                      <Link href={href || ''} key={value}>
                        <a className="block">
                          <MenuItem
                            key={value}
                            className={classNames({
                              'font-medium py-10 flex text-gray-600 pl-10': true,
                              'text-primary-500 bg-primary-100 hover:bg-blue-200': isActive(value),
                            })}
                            onClick={() => {
                              Analytics.track(`${label} Button Click`);
                            }}
                            icon={
                              <span
                                className={classNames({
                                  'text-primary-500': isActive(value),
                                  'text-gray-600': true,
                                })}
                              >
                                {icon}
                              </span>
                            }
                          >
                            <div className="flex">{label}</div>
                          </MenuItem>
                        </a>
                      </Link>
                    ))}
                  </div>
                )}
              </section> */}
              {router.query.mode === 'w' && <WorkspaceData handleImport={handleImport} />}

              {/* {router.query.mode === 'a' && <LMSWorkspaceData />} */}
              {showDefaultWorkspace && <SharedPinnedData />}
              {showDefaultWorkspace && memberRole !== 'guest' && <PrivatePinnedData />}

              {/* {frontendHostUrl !== 'https://app.sketchnote.co/' && <CRMData />} */}

              {/* <button
                onClick={() => router.push(`/w/${orgId}/template-store`)}
                className="flex my-12 text-gray-600 text-sm font-medium ml-12 items-center"
              >
                <TemplateStoreIcon className="text-gray-600 mr-8 text-lg" />
                Explore templates
              </button>

              {router.query.mode === 'w' && (
                <button
                  onClick={}
                  className="flex my-12 text-gray-600 text-sm font-medium ml-12 items-center"
                >
                  <ImportIcon className="text-gray-600 text-lg" />
                  <span className="ml-8">Import</span>
                </button>
              )} */}
            </div>
          )}
        </div>

        {memberRole === 'guest' ? (
          <section className="h-155 mx-auto py-20 px-24 border-t">
            <p className="text-sm text-gray-600">
              You&apos;re currently a guest in this organization and can only see files workspaces
              and tasks you&apos;ve been invited to
            </p>
            {/* <button
              className="text-blue-500 font-medium text-sm mt-14"
              onClick={() => {
                onChangeOrganisation(ownerOrganisation);
              }}
            >
              Switch to your organization
            </button> */}
          </section>
        ) : (
          <>
            <button
              onClick={() => router.push(`/w/${orgId}/template-store`)}
              className="flex text-gray-600 text-sm bg-gray-100 font-medium items-center p-8 rounded-md m-14"
            >
              <TemplateStoreNewIcon className="text-gray-600 mr-8 text-lg" />
              Explore templates
            </button>
            {router.query.mode === 'w' && (
              <section className="h-58 border-t border-gray-200 px-14 py-8">
                <Tippy
                  content={'Add workspace to create a file'}
                  className="w-auto h-auto bg-gray-999"
                  placement="bottom"
                  disabled={workspaces?.length > 0}
                >
                  <span>
                    <button
                      className={cn({
                        'my-auto py-12 flex justify-around text-white rounded-sm font-medium w-full items-center':
                          true,
                        'bg-blue-500 ': workspaces.length > 0,
                        'bg-gray-500 ': workspaces.length === 0,
                      })}
                      tabIndex={-1}
                      onClick={() => {
                        authData?.memberInformation?.productTour?.files
                          ? handleOpenProductTour()
                          : onNewFileOpen();
                      }}
                      disabled={workspaces.length === 0}
                    >
                      <div className="flex mx-auto">
                        <AddIcon className="text-xs mt-6 text-white" />
                        &nbsp;&nbsp;
                        <span className="block text-white">Start new file</span>
                      </div>
                    </button>
                  </span>
                </Tippy>
              </section>
            )}

            {/* To be replaced with catalogue sidebar */}

            {/* {router.query.mode === 'a' && (
              <section className="h-58 px-14  pt-10 pb-20">
                <Link
                  href={
                    authData?.authentication === 'authorised'
                      ? `/${mode}/${orgId}/catalog`
                      : `/catalog`
                  }
                >
                  <a className="">
                    <button
                      className="bg-primary-999 w-full py-14 rounded-sm hover:bg-blue-999 active:bg-blue-999 text-center text-white"
                      tabIndex={-1}
                      onClick={() => {
                        Analytics.track('Find new lesson Click');
                      }}
                      style={{ backgroundColor: '#13183B' }}
                    >
                      <ShoppingCartIcon />
                      &nbsp;Lesson library
                    </button>
                  </a>
                </Link>
              </section>
            )} */}
          </>
        )}

        <Modal isOpen={isProductTourModalOpen} onClose={handleCloseProductTourModal}>
          <ModalOverlay />
          <ModalContent
            style={{
              maxWidth: '50vw',
              padding: '32px',
              marginTop: '6rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <video
              playsInline
              loop
              controls
              preload="auto"
              width="100%"
              className="shadow-mdl2 rounded-md"
            >
              <source
                type="video/mp4"
                src="https://res.cloudinary.com/samvibes/video/upload/v1700819668/onboarding-videos/File_walkthrough_Nov.mp4"
              />
              {/* Add webm links */}
              <source
                type="video/webm"
                src="https://res.cloudinary.com/samvibes/video/upload/v1700819668/onboarding-videos/File_walkthrough_Nov.mp4"
              />
              Sorry, your browser doesn’t support embedded videos.
            </video>

            <div className="mt-24 w-full">
              <div className="flex flex-col justify-center mx-auto">
                <p className="text-blue-800 font-semibold text-xl ">
                  Your projects’ new home!
                  <span role="img" aria-label="partying face">
                    &nbsp;📁
                  </span>
                </p>
              </div>
              <p className="text-sm text-gray-600">
                The brand new file system hosts all your work—from docs to kanban boards and more.
              </p>
            </div>
          </ModalContent>
        </Modal>

        <Modal isOpen={isNewFileOpen} onClose={onNewFileClose}>
          <ModalOverlay />
          <ModalContent style={{ maxWidth: '700px', maxHeight: '500px', borderRadius: '8px' }}>
            <ModalCloseButton />
            <StartNewFile onClose={onNewFileClose} />
          </ModalContent>
        </Modal>

        {/* CSV import modal start */}
        <Modal isOpen={!!isCsvImportModalOpen} onClose={() => setIsCsvImportModalOpen(false)}>
          <ModalOverlay />
          <ModalContent
            style={{
              maxWidth: '46.4rem',
              borderRadius: '0.8rem',
              margin: 'auto',
              backgroundColor: 'white',
              padding: '32px',
            }}
          >
            <ModalCloseButton className="text-xs text-gray-500 m-24" />
            <ModalBody>
              <CsvImportSelectFile
                selectedWorkspaceId={
                  typeof isCsvImportModalOpen === 'string' ? isCsvImportModalOpen : undefined
                }
                onClose={() => {
                  return;
                }}
                showCsvMap={onShowCsvMap}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* CSV map modal open */}
        {importFile && importFile[0]?.name ? (
          <Modal isOpen={isCsvMapModalOpen} onClose={() => setIsCsvMapModalOpen(false)}>
            <ModalOverlay />
            <ModalCloseButton />
            <ModalContent
              maxHeight="auto"
              className="static rounded-lg"
              style={{ maxWidth: '102.4rem', borderRadius: '8px' }}
            >
              <CsvFileMap
                file={importFile}
                fileTitle={fileTitle}
                workspace={workspace.value ? workspace.value : undefined}
                onClose={() => setIsCsvMapModalOpen(false)}
              />
            </ModalContent>
          </Modal>
        ) : (
          <></>
        )}
      </>
    </SidebarWrapper>
  );
};
